/**
 * Image Gallery Management Hook
 * Manages image uploads and notes per service instance
 */

import { useCallback, useState } from "react";

// PhotoData type - matches PhotoUpload component
export interface PhotoData {
  url: string;
  caption?: string;
}

export const useImageGallery = () => {
  const [imageGalleryNotes, setImageGalleryNotes] = useState<Record<number, string>>({});
  const [imageGalleryImages, setImageGalleryImages] = useState<Record<number, PhotoData[]>>({});
  const [imageGalleryExpanded, setImageGalleryExpanded] = useState<Record<number, boolean>>({});

  /**
   * Set notes for an instance
   */
  const setInstanceNotes = useCallback((instanceId: number, notes: string) => {
    setImageGalleryNotes((prev) => ({
      ...prev,
      [instanceId]: notes,
    }));
  }, []);

  /**
   * Get notes for an instance
   */
  const getInstanceNotes = useCallback(
    (instanceId: number): string => {
      return imageGalleryNotes[instanceId] || "";
    },
    [imageGalleryNotes]
  );

  /**
   * Set images for an instance
   * Accepts either PhotoData objects or string URLs (for backward compatibility)
   */
  const setInstanceImages = useCallback((instanceId: number, images: (PhotoData | string)[]) => {
    // Normalize to PhotoData objects
    const normalizedImages = images.map((img) => (typeof img === "string" ? { url: img } : img));
    setImageGalleryImages((prev) => ({
      ...prev,
      [instanceId]: normalizedImages,
    }));
  }, []);

  /**
   * Get images for an instance
   */
  const getInstanceImages = useCallback(
    (instanceId: number): PhotoData[] => {
      return imageGalleryImages[instanceId] || [];
    },
    [imageGalleryImages]
  );

  /**
   * Add an image to an instance
   * Accepts either PhotoData object or string URL
   */
  const addInstanceImage = useCallback((instanceId: number, image: PhotoData | string) => {
    const photoData = typeof image === "string" ? { url: image } : image;
    setImageGalleryImages((prev) => ({
      ...prev,
      [instanceId]: [...(prev[instanceId] || []), photoData],
    }));
  }, []);

  /**
   * Remove an image from an instance
   */
  const removeInstanceImage = useCallback((instanceId: number, imageUrl: string) => {
    setImageGalleryImages((prev) => ({
      ...prev,
      [instanceId]: (prev[instanceId] || []).filter((photo) => photo.url !== imageUrl),
    }));
  }, []);

  /**
   * Toggle gallery expansion for an instance
   */
  const toggleGalleryExpanded = useCallback((instanceId: number) => {
    setImageGalleryExpanded((prev) => ({
      ...prev,
      [instanceId]: !prev[instanceId],
    }));
  }, []);

  /**
   * Check if gallery is expanded for an instance
   */
  const isGalleryExpanded = useCallback(
    (instanceId: number): boolean => {
      return imageGalleryExpanded[instanceId];
    },
    [imageGalleryExpanded]
  );

  /**
   * Clear gallery data for an instance
   */
  const clearInstanceGallery = useCallback((instanceId: number) => {
    setImageGalleryNotes((prev) => {
      const { [instanceId]: removed, ...rest } = prev;
      return rest;
    });
    setImageGalleryImages((prev) => {
      const { [instanceId]: removed, ...rest } = prev;
      return rest;
    });
    setImageGalleryExpanded((prev) => {
      const { [instanceId]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  /**
   * Load gallery data (for editing existing quotes)
   * Accepts either PhotoData objects or string URLs (for backward compatibility)
   */
  const loadGalleryData = useCallback(
    (
      notes: Record<number, string>,
      images: Record<number, (PhotoData | string)[]>,
      expanded?: Record<number, boolean>
    ) => {
      // Normalize all images to PhotoData objects
      const normalizedImages: Record<number, PhotoData[]> = {};
      Object.entries(images).forEach(([instanceId, imgs]) => {
        normalizedImages[Number(instanceId)] = imgs.map((img) =>
          typeof img === "string" ? { url: img } : img
        );
      });

      setImageGalleryNotes(notes);
      setImageGalleryImages(normalizedImages);
      if (expanded) {
        setImageGalleryExpanded(expanded);
      }
    },
    []
  );

  /**
   * Get all gallery data (for save/export)
   */
  const getAllGalleryData = useCallback(() => {
    return {
      notes: imageGalleryNotes,
      images: imageGalleryImages,
    };
  }, [imageGalleryNotes, imageGalleryImages]);

  return {
    // Notes management
    setInstanceNotes,
    getInstanceNotes,

    // Images management
    setInstanceImages,
    getInstanceImages,
    addInstanceImage,
    removeInstanceImage,

    // Expansion state
    toggleGalleryExpanded,
    isGalleryExpanded,

    // Bulk operations
    clearInstanceGallery,
    loadGalleryData,
    getAllGalleryData,
  };
};
